import React, { useCallback, useState } from 'react';
import { Grid, Table, TableBody, TableHead, TableRow, Typography } from '@material-ui/core';
import TableCell from 'src/components/TableCell';
import Card from 'src/components/Card';
import { createUseStyles } from 'src/theming';
import moment from 'moment';
import DateRangeContainer from '../../components/DateRangeContainer';
import useDealsStats from './hooks/useDealsStats';
import { numberWithSpaces } from 'src/utils/NumberUtils';
import LegacyAgencySelect from 'src/components/LegacyAgencySelect';
import { LegacyAgency } from '../../../../../../interfaces/api/Agency';

const useStyles = createUseStyles({
  filterContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  agencySelect: {
    minWidth: '200px !important',
  },
});

const DealsGlobalReport = () => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState<string | null>(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string | null>(moment().endOf('month').format('YYYY-MM-DD'));
  const [agencyId, setAgencyId] = useState<LegacyAgency['id']>(1);
  const [stats] = useDealsStats(agencyId, startDate || undefined, endDate || undefined);

  const getDealsTotal = useCallback(() => {
    if (!stats) {
      return 0;
    }

    return (
      parseInt(stats.dealsWonCount || '0') +
      parseInt(stats.dealsLostCount || '0') +
      parseInt(stats.dealsOngoingCount || '0')
    );
  }, [stats]);

  const getDealsPdcTotal = useCallback(() => {
    if (!stats) {
      return 0;
    }

    return numberWithSpaces(
      parseFloat(stats?.dealsWonPDC || '0') +
        parseFloat(stats?.dealsLostPDC || '0') +
        parseFloat(stats?.dealsOngoingPDC || '0')
    );
  }, [stats]);

  const getDealsRatio = (count: number) => {
    if (!stats) {
      return 0;
    }

    const total =
      parseInt(stats.dealsWonCount || '0') +
      parseInt(stats.dealsLostCount || '0') +
      parseInt(stats.dealsOngoingCount || '0');

    if (total <= 0) {
      return 0;
    }

    return ((count * 100) / total).toFixed(2);
  };

  return (
    <Card>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant='h6' id='tableTitle' component='div'>
            Portefeuille d'affaires
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <DateRangeContainer
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Grid>

        <Grid item xs={12} sm={6} className={classes.filterContainer}>
          <LegacyAgencySelect className={classes.agencySelect} agencyId={agencyId} onChange={setAgencyId} />
        </Grid>

        <Grid item xs={12} style={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Affaires</TableCell>
                <TableCell align='center'>Ratio</TableCell>
                <TableCell align='center'>Nb</TableCell>
                <TableCell align='right'>Pdc</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell>Gagnées</TableCell>
                <TableCell align='center'>{getDealsRatio(parseInt(stats?.dealsWonCount || '0'))}%</TableCell>
                <TableCell align='center'>{stats?.dealsWonCount}</TableCell>
                <TableCell align='right'>{numberWithSpaces(parseFloat(stats?.dealsWonPDC || '0'))}€</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>En cours</TableCell>
                <TableCell align='center'>{getDealsRatio(parseInt(stats?.dealsOngoingCount || '0'))}%</TableCell>
                <TableCell align='center'>{stats?.dealsOngoingCount}</TableCell>
                <TableCell align='right'>{numberWithSpaces(parseFloat(stats?.dealsOngoingPDC || '0'))}€</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Perdu</TableCell>
                <TableCell align='center'>{getDealsRatio(parseInt(stats?.dealsLostCount || '0'))}%</TableCell>
                <TableCell align='center'>{stats?.dealsLostCount}</TableCell>
                <TableCell align='right'>{numberWithSpaces(parseFloat(stats?.dealsLostPDC || '0'))}€</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                <TableCell align='center'>
                  <strong>100%</strong>
                </TableCell>
                <TableCell align='center'>
                  <strong>{getDealsTotal()}</strong>
                </TableCell>
                <TableCell align='right'>
                  <strong>{getDealsPdcTotal()}€</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Card>
  );
};

export default DealsGlobalReport;
